import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import {
  Users,
  TrendingUp,
  Target,
  DollarSign,
  Sparkles,
} from "lucide-react";

import { useCustomers } from "@/features/customers/hooks";
import { useDeals } from "@/features/customers/deal-hooks";
import { openDeals, wonDeals, sumValue } from "@/features/customers/deal-utils";
import { formatCurrency } from "@/lib/format";
import { customerDisplayName } from "@/features/customers/customer-utils";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { CustomerStatus } from "@/types/customers";

interface StatCardProps {
  title: string;
  value: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  loading?: boolean;
  /** Route the card links to (the list behind the number). */
  to?: { to: string; search?: Record<string, unknown> };
}

function StatCard({ title, value, hint, icon: Icon, loading, to }: StatCardProps) {
  const card = (
    <Card className={to ? "transition-colors hover:bg-accent/50" : undefined}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-7 w-20" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
        {hint && (
          <p className="pt-1 text-xs text-muted-foreground">{hint}</p>
        )}
      </CardContent>
    </Card>
  );
  return to ? (
    <Link to={to.to} search={to.search as never}>
      {card}
    </Link>
  ) : card;
}

type ActivityItem = {
  id: string;
  kind: "customer" | "deal";
  text: string;
  meta: string;
  date: string;
  href: string;
};

export function DashboardTab() {
  const { data: customers, isLoading } = useCustomers();
  const { data: deals = [] } = useDeals();

  const customerKpis = useMemo(() => {
    const list = customers ?? [];
    const byStatus = list.reduce(
      (acc, c) => {
        acc[c.status] = (acc[c.status] ?? 0) + 1;
        return acc;
      },
      {} as Record<CustomerStatus, number>
    );
    return {
      total: list.length,
      leads: byStatus.lead ?? 0,
      active: byStatus.active ?? 0,
      inactive: byStatus.inactive ?? 0,
    };
  }, [customers]);

  const dealKpis = useMemo(() => {
    const open = openDeals(deals);
    const won = wonDeals(deals);
    return {
      openCount: open.length,
      pipelineValue: sumValue(open),
      wonCount: won.length,
      wonValue: sumValue(won),
    };
  }, [deals]);

  const activity = useMemo<ActivityItem[]>(() => {
    const fromCustomers: ActivityItem[] = (customers ?? []).map((c) => ({
      id: `c-${c.id}`,
      kind: "customer" as const,
      text: `${customerDisplayName(c)} added as ${c.status}`,
      meta: c.company_name || c.contact_email,
      date: c.created_at,
      href: `/sales/${c.id}`,
    }));
    const fromDeals: ActivityItem[] = deals.map((d) => ({
      id: `d-${d.id}`,
      kind: "deal" as const,
      text: `${d.title} — ${formatCurrency(d.value)}`,
      meta: `${d.customer ? customerDisplayName(d.customer) : "—"} · ${d.owner}`,
      date: d.created_at,
      href: `/sales/deals/${d.id}`,
    }));
    return [...fromCustomers, ...fromDeals]
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .slice(0, 6);
  }, [customers, deals]);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Customers"
          value={String(customerKpis.total)}
          hint={`${customerKpis.active} active`}
          icon={Users}
          loading={isLoading}
          to={{ to: "/sales", search: { tab: "customers" } }}
        />
        <StatCard
          title="Active Leads"
          value={String(customerKpis.leads)}
          hint="Customers in lead status"
          icon={TrendingUp}
          loading={isLoading}
          to={{ to: "/sales", search: { tab: "customers" } }}
        />
        <StatCard
          title="Open Deals"
          value={String(dealKpis.openCount)}
          hint={`${dealKpis.wonCount} won all-time`}
          icon={Target}
          to={{ to: "/sales", search: { tab: "deals" } }}
        />
        <StatCard
          title="Pipeline Value"
          value={formatCurrency(dealKpis.pipelineValue)}
          hint={`${formatCurrency(dealKpis.wonValue)} won`}
          icon={DollarSign}
          to={{ to: "/sales", search: { tab: "deals" } }}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4" /> Recent Activity
            </CardTitle>
            <CardDescription>
              Latest customers and deals
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-1">
            {activity.map((item) => (
              <Link
                key={item.id}
                to={item.href}
                className="flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted/50"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                  {item.kind === "customer" ? (
                    <Users className="h-4 w-4" />
                  ) : (
                    <DollarSign className="h-4 w-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{item.text}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {item.meta}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {new Date(item.date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
